import { Link } from 'react-router-dom';

import { cn } from '@core/lib/cn';

export interface StepperStep {
  /** Stable id, matching the route segment (e.g. "review"). */
  id: string;
  /** Short label. At 375px only the current one is spelled out. */
  label: string;
  /** Route to navigate to. Omit for steps not yet reachable. */
  to?: string;
  /** A step the user has completed and may go back to. */
  complete: boolean;
}

export interface StepperProps {
  steps: StepperStep[];
  /** Index of the current step. */
  currentIndex: number;
  className?: string;
}

/**
 * Checkout progress indicator.
 *
 * At 375px there is no room for six labels, so this renders dots plus
 * "Step 3 of 6 · Options" — the shape of a progress bar with the precision of a
 * label. It is an <ol> with `aria-current="step"` on the active item, which is
 * what a screen reader needs to say "3 of 6".
 *
 * Completed steps are LINKS (going back to change an option is a normal thing to
 * want); future steps are inert spans, because letting someone jump to /order/pay
 * before uploading a file produces a broken page.
 */
export function Stepper({ steps, currentIndex, className }: StepperProps) {
  const current = steps[currentIndex];
  const total = steps.length;

  return (
    <nav aria-label="Checkout progress" className={cn('flex flex-col gap-2', className)}>
      <p className="text-sm font-medium text-ink-muted">
        Step {Math.min(currentIndex + 1, total)} of {total}
        {current ? <span className="text-ink"> &middot; {current.label}</span> : null}
      </p>

      {/* role="list" is redundant per the HTML spec but NOT per Safari: VoiceOver
          drops list semantics from any list styled with `list-style: none`, which
          every list in this app is. Keeping the role is the documented workaround. */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ol className="flex list-none items-center gap-1.5" role="list">
        {steps.map((step, index) => {
          const isCurrent = index === currentIndex;
          const state = isCurrent ? 'current' : step.complete ? 'complete' : 'upcoming';
          const dot = (
            <span
              className={cn(
                'block h-2 rounded-full transition-colors',
                // Gold on a near-white track is 1.57:1, so the current step
                // gets the outline treatment and the completed step uses the
                // darker gold outright. Width still differs too: state is
                // never carried by colour alone (WCAG 1.4.1).
                isCurrent ? 'w-8 bg-brand ring-1 ring-inset ring-brand-edge' : 'w-full',
                state === 'complete' && 'bg-brand-edge',
                state === 'upcoming' && 'bg-surface-sunken ring-1 ring-inset ring-line',
              )}
            />
          );

          return (
            <li
              key={step.id}
              className={cn('flex-1', isCurrent && 'flex-none')}
              aria-current={isCurrent ? 'step' : undefined}
            >
              {step.complete && step.to && !isCurrent ? (
                <Link
                  to={step.to}
                  // 44px tap target via padding while the dot itself stays 8px tall.
                  className="-my-4 flex min-h-touch items-center py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 rounded-full"
                >
                  <span className="sr-only">Go back to {step.label}</span>
                  {dot}
                </Link>
              ) : (
                <span className="-my-4 flex min-h-touch items-center py-4">
                  <span className="sr-only">
                    {step.label}
                    {isCurrent ? ' (current step)' : ' (not started)'}
                  </span>
                  {dot}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
